import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Save, Sparkles, ImagePlus, Pencil, Trash2, Plus, Crown, Lock, Settings, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Profile() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState("");
  const [picture, setPicture] = useState("");
  const [genPrompt, setGenPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mine, setMine] = useState([]);
  const [loadingChars, setLoadingChars] = useState(true);

  // account settings
  const [username, setUsername] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [preferences, setPreferences] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [savingAccount, setSavingAccount] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setPicture(user.picture || "");
      setUsername(user.username || "");
      setGender(user.gender || "");
      setAge(user.age != null ? String(user.age) : "");
      setPreferences(user.preferences || "");
    }
  }, [user]);

  const loadMine = () => {
    setLoadingChars(true);
    api.get("/characters/mine").then((res) => {
      setMine(res.data);
      setLoadingChars(false);
    });
  };
  useEffect(loadMine, []);

  const generateAvatar = async () => {
    const prompt = genPrompt.trim() || `portrait avatar of ${name}`;
    setGenerating(true);
    try {
      const res = await api.post("/characters/generate-avatar", { prompt });
      setPicture(res.data.avatar_url);
      toast.success("Avatar erstellt!");
    } catch (e) {
      toast.error("Avatar-Erstellung fehlgeschlagen");
    } finally {
      setGenerating(false);
    }
  };

  const saveProfile = async () => {
    if (!name.trim()) {
      toast.error("Name darf nicht leer sein");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/profile", { name: name.trim(), picture });
      await refreshUser();
      toast.success("Profil gespeichert");
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const saveAccount = async () => {
    const payload = {};
    if (username.trim()) payload.username = username.trim();
    if (gender) payload.gender = gender;
    if (preferences.trim()) payload.preferences = preferences.trim();
    if (age !== "") payload.age = parseInt(age, 10);
    if (newPassword) {
      if (newPassword.length < 6) {
        toast.error("Neues Passwort muss mind. 6 Zeichen haben");
        return;
      }
      payload.new_password = newPassword;
      payload.current_password = currentPassword;
    }
    setSavingAccount(true);
    try {
      await api.put("/auth/profile", payload);
      await refreshUser();
      setCurrentPassword("");
      setNewPassword("");
      toast.success("Einstellungen gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSavingAccount(false);
    }
  };

  const deleteChar = async (e, id, cname) => {
    e.stopPropagation();
    if (!window.confirm(`${cname} löschen? Alle Chats gehen verloren.`)) return;
    try {
      await api.delete(`/characters/${id}`);
      toast.success("Charakter gelöscht");
      setMine((m) => m.filter((c) => c.id !== id));
    } catch (err) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="min-h-screen bg-[#090A0F] grid-bg">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <button
          data-testid="profile-back"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-all"
        >
          <ArrowLeft className="w-4 h-4" /> Zurück
        </button>

        <h1 className="font-display text-2xl sm:text-3xl font-bold text-white mb-1">Mein Profil</h1>
        <p className="text-slate-400 text-sm mb-8">Passe deinen Namen und Avatar an</p>

        {/* Profile edit */}
        <div className="rounded-3xl bg-[#12141D] border border-white/10 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="relative flex-shrink-0">
              {picture ? (
                <img
                  src={picture}
                  alt="avatar"
                  className="w-24 h-24 rounded-2xl object-cover border border-white/10"
                  data-testid="profile-avatar-img"
                />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-indigo-600 flex items-center justify-center text-white text-3xl font-bold">
                  {name?.[0]?.toUpperCase() || "U"}
                </div>
              )}
            </div>

            <div className="flex-1 w-full space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Anzeigename</label>
                <input
                  data-testid="profile-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full mt-2 bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl px-4 py-3 text-white outline-none transition-all"
                />
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="profile-avatar-prompt"
                  value={genPrompt}
                  onChange={(e) => setGenPrompt(e.target.value)}
                  placeholder="Avatar beschreiben (KI)"
                  className="flex-1 bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-all text-sm"
                />
                <button
                  data-testid="profile-generate-avatar"
                  onClick={generateAvatar}
                  disabled={generating}
                  className="flex-shrink-0 flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/10 text-white font-medium rounded-xl px-4 active:scale-95 transition-all disabled:opacity-60 text-sm"
                >
                  <ImagePlus className="w-4 h-4" /> {generating ? "..." : "KI"}
                </button>
              </div>
            </div>
          </div>

          <button
            data-testid="profile-save-btn"
            onClick={saveProfile}
            disabled={saving}
            className="mt-6 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold rounded-xl py-3 shadow-lg shadow-violet-500/20 active:scale-95 transition-all disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {saving ? "Speichern..." : "Profil speichern"}
          </button>
        </div>

        {/* Account settings */}
        <div className="mt-6 rounded-3xl bg-[#12141D] border border-white/10 p-6 sm:p-8">
          <h2 className="font-display text-lg font-bold text-white flex items-center gap-2 mb-5">
            <Settings className="w-5 h-5 text-fuchsia-400" /> Konto-Einstellungen
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Benutzername</label>
              <input
                data-testid="settings-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="einzigartiger Name"
                className="w-full mt-2 bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-all"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Geschlecht</label>
              <Select value={gender} onValueChange={setGender}>
                <SelectTrigger data-testid="settings-gender" className="w-full mt-2 bg-[#0d0e15] border-white/10 text-white rounded-xl py-3 h-auto">
                  <SelectValue placeholder="Wählen..." />
                </SelectTrigger>
                <SelectContent className="bg-[#12141D] border-white/10 text-white">
                  <SelectItem value="weiblich">Weiblich</SelectItem>
                  <SelectItem value="männlich">Männlich</SelectItem>
                  <SelectItem value="divers">Divers</SelectItem>
                  <SelectItem value="keine Angabe">Keine Angabe</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Alter</label>
              <input
                data-testid="settings-age"
                type="number"
                min="13"
                max="120"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder="z.B. 24"
                className="w-full mt-2 bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-all"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Vorlieben</label>
              <input
                data-testid="settings-preferences"
                value={preferences}
                onChange={(e) => setPreferences(e.target.value)}
                placeholder="z.B. Fantasy, RPG, romantische Storys, Sci-Fi..."
                className="w-full mt-2 bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-all"
              />
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-white/5">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-4">
              <KeyRound className="w-4 h-4 text-slate-400" /> Passwort ändern
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {user?.has_password && (
                <input
                  data-testid="settings-current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Aktuelles Passwort"
                  className="w-full bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-all"
                />
              )}
              <input
                data-testid="settings-new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Neues Passwort (min. 6 Zeichen)"
                className="w-full bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-all"
              />
            </div>
          </div>

          <button
            data-testid="settings-save-btn"
            onClick={saveAccount}
            disabled={savingAccount}
            className="mt-6 w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/10 text-white font-semibold rounded-xl py-3 active:scale-95 transition-all disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {savingAccount ? "Speichern..." : "Einstellungen speichern"}
          </button>
        </div>

        {/* My characters */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-fuchsia-400" /> Meine Charaktere
            </h2>
            <button
              data-testid="profile-create-character"
              onClick={() => navigate("/create")}
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 text-sm rounded-full px-4 py-2 transition-all"
            >
              <Plus className="w-4 h-4" /> Neu
            </button>
          </div>

          {loadingChars ? (
            <div className="space-y-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="h-20 rounded-2xl bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : mine.length === 0 ? (
            <div className="text-center py-12 text-slate-500 rounded-2xl border border-white/5 bg-[#12141D]" data-testid="profile-no-characters">
              Du hast noch keine Charaktere erstellt.
            </div>
          ) : (
            <div className="space-y-3">
              {mine.map((c) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid={`profile-char-${c.id}`}
                  className="flex items-center gap-4 bg-[#131520] border border-white/5 hover:border-fuchsia-500/30 rounded-2xl p-4 transition-all"
                >
                  <img
                    src={c.avatar_url || "https://images.unsplash.com/photo-1559828801-04565cd31e27?crop=entropy&cs=srgb&fm=jpg&q=85&w=200"}
                    alt={c.name}
                    className="w-14 h-14 rounded-xl object-cover border border-white/10 flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display font-semibold text-white truncate">{c.name}</h3>
                      {c.is_vip && <Crown className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />}
                      {c.is_public === false && <Lock className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />}
                    </div>
                    <p className="text-sm text-slate-400 truncate">{c.title}</p>
                  </div>
                  <button
                    data-testid={`profile-edit-${c.id}`}
                    onClick={() => navigate(`/edit/${c.id}`)}
                    className="p-2.5 rounded-full text-slate-400 hover:text-fuchsia-300 hover:bg-fuchsia-500/10 transition-all"
                    title="Bearbeiten"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`profile-delete-${c.id}`}
                    onClick={(e) => deleteChar(e, c.id, c.name)}
                    className="p-2.5 rounded-full text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-all"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
